import React from 'react';
import { useBetaSignup } from '../context/BetaSignupContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { teamMembers } from '../mockData';

const About = () => {
  const { openModal } = useBetaSignup();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-light mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Built by investors,<br />
            for investors
          </h1>
          <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto">
            Our team combines decades of experience from leading Australian financial institutions
            with cutting-edge technology to deliver exceptional wealth management for HENWYs.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-light mb-8 text-center" style={{ fontFamily: 'Georgia, serif' }}>About Us</h2>
          <div className="space-y-6 text-lg text-gray-600 font-light leading-relaxed">
            <p>
              You may have heard of HENRY (High Earning, Not Rich Yet). We prefer to focus on <strong style={{ color: '#1a1a3e' }}>HENWY</strong> (High Earning, Not Wealthy Yet).
            </p>
            <p>
              HENWYs are high-earning professionals in their late 30s to late 40s, working 40+ hour weeks in demanding careers while juggling young families and aging parents. Their days blur between strategic meetings, school pickups, and late-night emails. They drive sensible cars, live in good school zones, and appear pretty successful.
            </p>
            <p className="text-xl text-[#1a1a3e] font-medium" style={{ fontFamily: 'Georgia, serif' }}>
              But privately, they feel like they're drowning.
            </p>
            <p>
              They watch assets inflate beyond reach while their savings stagnate. The fear that haunts them isn't poverty. It's the realisation that despite doing everything "right," they might never achieve the financial freedom their parents had. They rationalise their struggle as temporary ("once the kids are older," "after the next promotion"), but deep down know they're trapped in a cycle of earning well but never building wealth.
            </p>
            <p>
              Being a HENWY isn't about being bad with money. It's about being caught between an economy that rewards wealth with more wealth, and a financial system that was never designed for people in their situation.
            </p>
            <p className="text-xl text-[#1a1a3e] font-medium" style={{ fontFamily: 'Georgia, serif' }}>
              The system isn't broken. It's working exactly as designed.
            </p>
            <p>
              Over the past two decades, the average wealth of the top 20% has grown at 4x the rate of the lowest. Why? Because access to diversified, risk-appropriate assets equals performance. Sophisticated investors access private credit yielding 12-15%, commercial property generating 8-10%, and private equity delivering 17-20% annually. Meanwhile, retail investors are shoehorned into 6-7% returns through public markets and residential housing.
            </p>
            <p>
              This systematic exclusion of 3.4 million high-earning Australians from wealth-building asset classes represents the largest market inefficiency in modern financial services.
            </p>
            <p>
              Nobody built the bridge between "I make good money" and "I actually have wealth."
            </p>
            <p className="text-2xl text-[#1a1a3e] font-medium" style={{ fontFamily: 'Georgia, serif' }}>
              So we built Trove.
            </p>
            <p>
              Our platform opens the door to institutional-grade investments, blending the liquidity and transparency of listed assets with carefully selected unlisted investments. AI technology rebalances and optimises your portfolio, aiming for superior risk-adjusted returns.
            </p>
            <p>
              Our mission is simple: to reshape how future generations invest and build wealth in Australia.
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-light mb-12 text-center" style={{ fontFamily: 'Georgia, serif' }}>Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="p-6 text-center hover:shadow-xl transition-shadow bg-white border-0">
                <div className="w-32 h-32 rounded-full mx-auto mb-4 overflow-hidden bg-gray-200">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover grayscale"
                  />
                </div>
                <h3 className="font-light text-xl mb-1" style={{ fontFamily: 'Georgia, serif' }}>{member.name}</h3>
                <p className="text-sm text-[#FF8C42] mb-3 font-medium">{member.role}</p>
                <p className="text-sm text-gray-500 font-light leading-relaxed">{member.bio}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-light mb-12 text-center" style={{ fontFamily: 'Georgia, serif' }}>Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 border-0">
              <h3 className="text-3xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Transparency</h3>
              <p className="text-gray-600 font-light leading-relaxed">
                Every investment, every fee, every decision - fully transparent and explained
                in plain English. No hidden costs, no surprises. You deserve to know exactly where your money is invested.
              </p>
            </Card>
            <Card className="p-8 border-0">
              <h3 className="text-3xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Accessibility</h3>
              <p className="text-gray-600 font-light leading-relaxed">
                Institutional-grade wealth management shouldn't require a six-figure minimum.
                We've democratised access to alternative investments, starting at just $500.
              </p>
            </Card>
            <Card className="p-8 border-0">
              <h3 className="text-3xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Innovation</h3>
              <p className="text-gray-600 font-light leading-relaxed">
                We leverage AI and cutting-edge technology to deliver superior returns and solve
                problems that have held back retail investors for decades.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to build real wealth?
          </h2>
          <p className="text-xl text-gray-400 mb-8 font-light">
            Join our beta program for early access to institutional-grade investing.
          </p>
          <Button 
            size="lg" 
            onClick={openModal}
            className="bg-[#FF8C42] text-white hover:bg-[#FFB366] px-10 py-7 rounded-full font-light"
          >
            JOIN THE BETA
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
